import React, { Dispatch, SetStateAction, useLayoutEffect, useRef, useState } from "react";

import Link from "next/link";
import { useRouter } from "next/router";

import { PlusCircleOutlined } from "@ant-design/icons";
import { VirtualTable } from "@gsmvalve/gsmvalve-table";
import { t } from "@lingui/macro";
import { Button, Spin } from "antd";
import { isEmpty, keyBy } from "lodash";
import styled from "styled-components";

import { PermissionDetails } from "components/permissions/edit-link";
import {
  Column,
  generateColumns,
  generateRecords,
  mergePermissions,
  onSelectCell,
  onSelectColumn,
  onSelectRow,
  onSelectTable,
} from "components/permissions/utils";
import { EmptyTable } from "components/table";
import { TableContainer } from "components/table/styles";

import { useWindowSize } from "hooks/window";

const EmptyContainer = styled.div`
  padding: 24px;
  min-height: 400px;
`;

interface PermissionsProps {
  permissionDetails: PermissionDetails;
  updatedPermission: { [key: string]: { has_permission: boolean } };
  setUpdatePermissions: Dispatch<SetStateAction<any>>;
  columns: Column[];
  records: { permissions: any[]; owners: object[]; iteratee: string };
  withPermission: boolean;
  dataIndex: string;
  loading: boolean;
  offSetTop: number;
}

const PermissionsTable = ({
  permissionDetails,
  updatedPermission,
  setUpdatePermissions,
  columns,
  records,
  withPermission,
  dataIndex,
  loading,
  offSetTop,
}: PermissionsProps) => {
  const router = useRouter();
  const { height } = useWindowSize();
  const tableRef = useRef(null);
  const [tableHeight, setTableHeight] = useState(500);

  useLayoutEffect(() => {
    // @ts-ignore
    const tableHeight = height - (tableRef?.current?.parentNode.offsetTop + 180 - offSetTop);
    setTableHeight(tableHeight);
  }, [height, records]);

  const { search } = router.query;
  const permissions = mergePermissions(records.permissions, records.owners, updatedPermission, records.iteratee);
  const generatedRecords = generateRecords(permissions, records?.owners, records.iteratee);
  const searchValue = search?.toString().toLowerCase();

  const selectColumn = (key: string, value: boolean, permissions: { [key: string]: any }[]) =>
    onSelectColumn(key, value, permissions, updatedPermission, setUpdatePermissions, records.iteratee);

  const selectCell = (owner: string, key: string, value: boolean) =>
    onSelectCell(owner, key, value, updatedPermission, setUpdatePermissions);

  const selectRow = (owner: string, value: boolean, permissions: { [key: string]: any }[]) =>
    onSelectRow(owner, value, columns, permissions, updatedPermission, setUpdatePermissions);

  const getOwner = (permission: { [p: string]: string }) => {
    const ownerURL = permission[records.iteratee];
    return records.owners.find((owner: { [key: string]: any }) => {
      return (owner["url"] as string) === ownerURL;
    }) as { name: string; name_display: string };
  };

  const selectTable = (value: boolean) =>
    onSelectTable(value, columns, permissions, updatedPermission, setUpdatePermissions, records.iteratee);

  const filteredPermissions = withPermission
    ? permissions.filter((permission) => {
        const owner = getOwner(permission);
        const label = (owner?.name_display || owner?.name || "")?.toLowerCase()?.toString();

        if (searchValue) {
          return searchValue && label?.includes(searchValue);
        }

        const objPermissions: { has_permission: boolean }[] = Object.values(permission.permissions);
        return objPermissions?.some((p) => p?.has_permission === true);
      })
    : permissions.filter((permission) => {
        const owner = getOwner(permission);
        const label = (owner?.name_display || owner?.name || "")?.toLowerCase()?.toString();

        if (searchValue) {
          return searchValue && label?.includes(searchValue);
        }

        return true;
      });

  const columns_ = generateColumns(
    permissionDetails,
    dataIndex,
    columns,
    filteredPermissions,
    filteredPermissions?.length,
    selectColumn,
    selectCell,
    selectRow,
    selectTable
  );

  const keyedRecords = keyBy(records.permissions, records.iteratee);
  const dataSource = generatedRecords?.filter((record: any) => {
    const permissionNameValue = record?.[dataIndex]?.toLowerCase();

    const searched = searchValue && permissionNameValue?.includes(searchValue);

    if (searchValue) {
      return searched;
    }

    if (withPermission) {
      return (
        (record.permissions &&
          Object.keys(record.permissions).some((key: string) => {
            const row = record.permissions[key];
            return keyedRecords[record.url]?.permissions[key]?.has_permission === true
              ? true
              : row?.read_only === false;
          })) ||
        searched
      );
    }

    return true;
  });

  const fixedColumn = columns_?.map((column: any, i: number) => {
    if (i === 0) {
      return { ...column, width: 270, fixed: true };
    }

    return { ...column, width: 0 };
  });

  if (isEmpty(dataSource) && !loading) {
    return (
      <EmptyContainer>
        <EmptyTable
          height={window.innerHeight - 240}
          message={t`No additional fields defined for vehicles`}
          extra={
            <Link href={window.location.pathname}>
              <Button icon={<PlusCircleOutlined />} type="primary">
                {t`click here to edit permissions`}
              </Button>
            </Link>
          }
        />
      </EmptyContainer>
    );
  }

  return (
    <Spin spinning={loading || fixedColumn.length === 1}>
      <TableContainer ref={tableRef} disableheader={false} headerMarginRight={"-10.5px"}>
        {isEmpty(dataSource) ? (
          <EmptyTable height={tableHeight} />
        ) : (
          <VirtualTable
            dataSource={dataSource}
            originalRecords={dataSource}
            columns={fixedColumn}
            loading={false}
            height={tableHeight}
            defaultColumWidth={130}
          />
        )}
      </TableContainer>
    </Spin>
  );
};

export default PermissionsTable;
